import {
  Component,
  For,
  Show,
  createSignal,
  createEffect,
  createMemo,
  onCleanup,
} from "solid-js";
import { A } from "@solidjs/router";
import { TypeIcon } from "../work-items/TypeIcon";
import { useProjectPrefix } from "../../contexts/ProjectContext";
import type { EpicProgress, ColumnWithStatuses } from "../../api/types";

export interface EpicFilterRailProps {
  /** List of epics with progress data. Empty array = rail hidden. */
  epics: EpicProgress[];
  /** Currently selected epic ID, or null for "All items" (unscoped). */
  selectedEpicId: string | null;
  /** Callback when user selects an epic or clears the filter. */
  onSelect: (epicId: string | null) => void;
  /** Show loading skeleton while epics are being fetched. */
  loading?: boolean;
  /** Board columns for resolving epic status categories. */
  columns?: ColumnWithStatuses[];
}

type EpicCategory = "in_progress" | "todo" | "done";

/**
 * Build a lookup of which status_ids belong to "done" board columns.
 * Used only to detect when an epic has been explicitly marked Done.
 */
function buildDoneStatusSet(columns: ColumnWithStatuses[]): Set<string> {
  const doneIds = new Set<string>();
  for (const col of columns) {
    if (col.column_type === "done") {
      for (const sid of col.status_ids) doneIds.add(sid);
    }
  }
  return doneIds;
}

/**
 * Derive an epic's effective category from its child progress counts.
 *
 * The epic's own status_id is a lagging indicator (manually updated).
 * Child progress is a leading indicator (updates automatically).
 * We use children to derive the category, with one override:
 * an explicit Done status is authoritative (human decision).
 *
 * Rules:
 * 1. Epic explicitly in Done column → "done"
 * 2. Any child in_progress → "in_progress"
 * 3. Some children done + some remaining → "in_progress" (has momentum)
 * 4. All children complete (none todo/in_progress) → "done"
 * 5. Default (no children, or all children todo) → "todo"
 */
function deriveEpicCategory(
  epic: EpicProgress,
  doneStatuses: Set<string>,
): EpicCategory {
  // Rule 1: Explicit Done status is authoritative
  if (doneStatuses.has(epic.status_id)) return "done";

  // Rule 2: Any child actively in progress
  if (epic.in_progress > 0) return "in_progress";

  // Rule 3: Partial completion — some done, some remaining
  if (epic.done > 0 && (epic.todo > 0 || epic.cancelled > 0))
    return "in_progress";

  // Rule 4: All children complete (done + cancelled, nothing remaining)
  if (epic.total > 0 && epic.todo === 0 && epic.in_progress === 0)
    return "done";

  // Rule 5: Not started (no children, or all children still todo)
  return "todo";
}

const CATEGORY_ORDER: Record<EpicCategory, number> = {
  in_progress: 0,
  todo: 1,
  done: 2,
};

function completionPct(epic: EpicProgress): number {
  return epic.total > 0 ? epic.done / epic.total : 0;
}

const chipBase =
  "inline-flex items-center h-6 gap-1.5 px-2 text-xs font-medium rounded-sm border whitespace-nowrap flex-shrink-0 transition-colors duration-150 focus-ring cursor-pointer hit-area";
/** Shared 24px chip style for "All items" and epic pills. */
const chip = (on: boolean) =>
  `${chipBase} ${on ? "bg-ink text-paper border-ink" : "bg-surface text-ink-2 border-line hover:bg-sunken hover:text-ink"}`;

/** Mini segmented progress bar for an epic pill. */
const MiniProgressBar: Component<{
  epic: EpicProgress;
  selected: boolean;
}> = (props) => {
  const pct = (n: number) =>
    props.epic.total > 0 ? (n / props.epic.total) * 100 : 0;

  return (
    <div
      class={`h-1 w-10 rounded-sm overflow-hidden flex ${
        props.selected ? "bg-ink-2" : "bg-line"
      }`}
    >
      <Show when={pct(props.epic.done) > 0}>
        <div
          class="h-full bg-signal-green"
          style={{ width: `${pct(props.epic.done)}%` }}
        />
      </Show>
      <Show when={pct(props.epic.in_progress) > 0}>
        <div
          class="h-full bg-signal-blue"
          style={{ width: `${pct(props.epic.in_progress)}%` }}
        />
      </Show>
    </div>
  );
};

/** Expanded progress summary shown below the rail when an epic is selected. */
const ProgressSummary: Component<{ epic: EpicProgress }> = (props) => {
  const pctDone = () =>
    props.epic.total > 0
      ? Math.round((props.epic.done / props.epic.total) * 100)
      : 0;

  return (
    <div class="px-4 py-1.5 bg-paper border-b border-line flex items-center gap-4 text-xs text-ink-2">
      {/* `truncate` sits on the inner span, not the link: `hit-area`'s
          ::before overflows its owner, and `overflow: hidden` on the link
          itself would clip the 26px pointer box away. */}
      <A
        href={`/items/${props.epic.id}?from=board`}
        class="flex min-w-0 max-w-[200px] font-medium text-ink hover:underline hit-area"
      >
        <span class="truncate">{props.epic.title}</span>
      </A>
      <div class="flex items-center gap-3 text-ink-2">
        <span class="inline-flex items-center gap-1">
          <span class="w-2 h-2 rounded-full bg-signal-green inline-block" />
          {props.epic.done} done
        </span>
        <Show when={props.epic.in_progress > 0}>
          <span class="inline-flex items-center gap-1">
            <span class="w-2 h-2 rounded-full bg-signal-blue inline-block" />
            {props.epic.in_progress} in progress
          </span>
        </Show>
        <Show when={props.epic.todo > 0}>
          <span class="inline-flex items-center gap-1">
            <span class="w-2 h-2 rounded-full bg-line-strong inline-block" />
            {props.epic.todo} to do
          </span>
        </Show>
        <Show when={props.epic.cancelled > 0}>
          <span class="inline-flex items-center gap-1">
            <span class="w-2 h-2 rounded-full bg-signal-red inline-block" />
            {props.epic.cancelled} cancelled
          </span>
        </Show>
      </div>
      <span class="font-mono tabular-nums text-ink ml-auto">{pctDone()}%</span>
    </div>
  );
};

/** Loading skeleton shown while epics are being fetched. */
const RailSkeleton: Component = () => (
  <div class="px-4 py-2 bg-surface border-b border-line">
    <div class="flex items-center gap-2">
      <div class="h-6 w-24 bg-sunken rounded-sm animate-pulse" />
      <div class="w-px h-5 bg-line" />
      <div class="h-6 w-36 bg-sunken rounded-sm animate-pulse" />
      <div class="h-6 w-32 bg-sunken rounded-sm animate-pulse" />
      <div class="h-6 w-28 bg-sunken rounded-sm animate-pulse" />
    </div>
  </div>
);

/** Pill for a single active epic. */
const EpicPill: Component<{
  epic: EpicProgress;
  selected: boolean;
  onSelect: (id: string | null) => void;
  done?: boolean;
}> = (props) => {
  const prefix = useProjectPrefix();

  return (
    <button
      type="button"
      role="radio"
      aria-checked={props.selected}
      aria-label={`${props.epic.title}: ${props.epic.done} of ${props.epic.total} done`}
      class={`${chip(props.selected)} ${
        props.done && !props.selected ? "opacity-60" : ""
      }`}
      onClick={() => props.onSelect(props.selected ? null : props.epic.id)}
    >
      {/* Done epics get a checkmark icon; active epics get the normal TypeIcon */}
      <Show
        when={props.done && !props.selected}
        fallback={<TypeIcon typeName="Epic" />}
      >
        <svg
          class="w-4 h-4 flex-shrink-0 text-signal-green"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </Show>
      <span class="font-mono tabular-nums">
        {prefix()}-{props.epic.item_number}
      </span>
      <span class="truncate max-w-[140px]">{props.epic.title}</span>
      <MiniProgressBar epic={props.epic} selected={props.selected} />
      <span
        class={`font-mono tabular-nums text-2xs ${props.selected ? "opacity-70" : ""}`}
      >
        {props.epic.done}/{props.epic.total} done
      </span>
    </button>
  );
};

/**
 * Horizontal filter rail for scoping the board to a specific epic.
 * Renders pill-shaped buttons: "All items" + one per epic.
 * Hides entirely when there are no epics and not loading.
 *
 * Smart sorting: in_progress epics first, then todo, sorted by completion %
 * ascending within each group. Done/cancelled epics are hidden behind a
 * "+N done" collapse chip.
 */
export const EpicFilterRail: Component<EpicFilterRailProps> = (props) => {
  let scrollRef: HTMLDivElement | undefined;
  const [isOverflowing, setIsOverflowing] = createSignal(false);
  const [showDone, setShowDone] = createSignal(false);

  const checkOverflow = () => {
    if (scrollRef) {
      setIsOverflowing(scrollRef.scrollWidth > scrollRef.clientWidth);
    }
  };

  const doneStatuses = createMemo(() =>
    buildDoneStatusSet(props.columns ?? []),
  );

  // Split and sort epics using child-progress-derived categories
  const categorizedEpics = createMemo(() => {
    const doneSet = doneStatuses();
    const active: EpicProgress[] = [];
    const done: EpicProgress[] = [];

    for (const epic of props.epics) {
      const category = deriveEpicCategory(epic, doneSet);
      if (category === "done") {
        done.push(epic);
      } else {
        active.push(epic);
      }
    }

    // Sort active: in_progress first, then todo
    // Within each group: completion % ascending (least complete first)
    // Tiebreaker: item_number ascending (stable, deterministic)
    active.sort((a, b) => {
      const catA = deriveEpicCategory(a, doneSet);
      const catB = deriveEpicCategory(b, doneSet);
      const catDiff = CATEGORY_ORDER[catA] - CATEGORY_ORDER[catB];
      if (catDiff !== 0) return catDiff;
      const pctDiff = completionPct(a) - completionPct(b);
      if (pctDiff !== 0) return pctDiff;
      return a.item_number - b.item_number;
    });

    // Sort done by item_number for stable ordering
    done.sort((a, b) => a.item_number - b.item_number);

    return { active, done };
  });

  const activeEpics = () => categorizedEpics().active;
  const doneEpics = () => categorizedEpics().done;

  // Auto-expand done section when selected epic is in done list
  createEffect(() => {
    const selectedId = props.selectedEpicId;
    if (!selectedId) return;
    if (doneEpics().some((e) => e.id === selectedId)) {
      setShowDone(true);
    }
  });

  createEffect(() => {
    // Re-check overflow whenever the epic count or showDone changes
    const _count = props.epics.length;
    const _show = showDone();
    void _count;
    void _show;
    requestAnimationFrame(checkOverflow);
  });

  createEffect(() => {
    if (!scrollRef) return;
    const observer = new ResizeObserver(checkOverflow);
    observer.observe(scrollRef);
    onCleanup(() => observer.disconnect());
  });

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    const container = scrollRef;
    if (!container) return;
    const buttons = Array.from(
      container.querySelectorAll<HTMLButtonElement>(
        'button[role="radio"], button[data-done-chip]',
      ),
    );
    const currentIndex = buttons.findIndex(
      (btn) => btn === document.activeElement,
    );
    if (currentIndex === -1) return;
    e.preventDefault();
    const nextIndex =
      e.key === "ArrowRight"
        ? (currentIndex + 1) % buttons.length
        : (currentIndex - 1 + buttons.length) % buttons.length;
    buttons[nextIndex].focus();
  };

  const isAllSelected = () => props.selectedEpicId === null;

  const selectedEpic = createMemo(() =>
    props.epics.find((e) => e.id === props.selectedEpicId),
  );

  return (
    <Show when={!props.loading} fallback={<RailSkeleton />}>
      <Show when={props.epics.length > 0}>
        <div class="bg-surface border-b border-line py-1.5">
          <div class="relative">
            <div
              ref={scrollRef}
              role="radiogroup"
              aria-label="Filter board by epic"
              class="flex items-center gap-2 overflow-x-auto scrollbar-hide px-4 py-px"
              onKeyDown={handleKeyDown}
            >
              {/* "All items" pill */}
              <button
                type="button"
                role="radio"
                aria-checked={isAllSelected()}
                aria-label="All items"
                class={chip(isAllSelected())}
                onClick={() => props.onSelect(null)}
              >
                <svg
                  class="w-4 h-4 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path d="M4 6h16M4 12h16M4 18h16" />
                </svg>
                All items
              </button>

              {/* Divider */}
              <div class="w-px h-5 bg-line flex-shrink-0" aria-hidden="true" />

              {/* Active epic pills (in_progress first, then todo) */}
              <For each={activeEpics()}>
                {(epic) => (
                  <EpicPill
                    epic={epic}
                    selected={props.selectedEpicId === epic.id}
                    onSelect={props.onSelect}
                  />
                )}
              </For>

              {/* Done collapse chip */}
              <Show when={doneEpics().length > 0}>
                {/* Divider between active and done */}
                <div
                  class="w-px h-5 bg-line flex-shrink-0"
                  aria-hidden="true"
                />

                <button
                  type="button"
                  data-done-chip
                  class={`${chipBase} bg-surface text-ink-3 border-line border-dashed hover:bg-sunken hover:text-ink`}
                  onClick={() => setShowDone((prev) => !prev)}
                  aria-expanded={showDone()}
                  aria-label={
                    showDone()
                      ? "Hide done epics"
                      : `Show ${doneEpics().length} done epics`
                  }
                >
                  {showDone() ? "Hide done" : `+${doneEpics().length} done`}
                </button>

                {/* Done epic pills (shown when expanded) */}
                <Show when={showDone()}>
                  <For each={doneEpics()}>
                    {(epic) => (
                      <EpicPill
                        epic={epic}
                        selected={props.selectedEpicId === epic.id}
                        onSelect={props.onSelect}
                        done
                      />
                    )}
                  </For>
                </Show>
              </Show>
            </div>

            {/* Right fade hint when scrollable */}
            <Show when={isOverflowing()}>
              <div class="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-surface to-transparent pointer-events-none" />
            </Show>
          </div>
        </div>
        <Show when={selectedEpic()}>
          {(epic) => <ProgressSummary epic={epic()} />}
        </Show>
      </Show>
    </Show>
  );
};
